<script setup lang="ts">
import {
  HPixSelectContent,
  HPixSelectGroup,
  HPixSelectItem,
  HPixSelectItemIndicator,
  HPixSelectItemText,
  HPixSelectLabel,
  HPixSelectPortal,
  HPixSelectRoot,
  HPixSelectScrollDownButton,
  HPixSelectScrollUpButton,
  HPixSelectSeparator,
  HPixSelectTrigger,
  HPixSelectValue,
  HPixSelectViewport,
} from "@pixelpoint-pattern-library-v2/base/components/headless/base/inputs/HPixSelect/index";
// Static icon imports (build-safe). PixIcon's name= resolution is dynamic and
// can't be analyzed in a build, so internal icons are imported directly.
import ChevronRightIcon from "@icons/ChevronRightIcon.svg?component";
import CheckIcon from "@icons/check.svg?component";
import type { AcceptableValue } from "reka-ui";
import { useI18n } from "vue-i18n";

type SelectItem = {
  value: string;
  label: string;
  disabled?: boolean;
  [key: string]: unknown;
};

type SelectGroup = {
  label?: string;
  items: SelectItem[];
};

type Props = {
  modelValue?: AcceptableValue;
  placeholder?: string;
  groups?: SelectGroup[];
  items?: SelectItem[];
  disabled?: boolean;
  multiple?: boolean;
  hideItemIndicator?: boolean;
  required?: boolean;
  /**
   * Validation message. When set, the trigger is flagged invalid (`aria-invalid`,
   * `data-error`) and the message is rendered below it, linked via `aria-describedby`.
   */
  error?: string;
  /** Visible field label rendered above the trigger. When provided it supplies
   *  the trigger's accessible name (associated via `for`/`id`), so `ariaLabel`
   *  is only needed when the select is rendered without a visible label. */
  label?: string;
  /**
   * Accessible name for the trigger. Required for a11y when the select has no
   * associated visible <label> — without it screen readers announce an unnamed
   * combobox (axe: button-name). Forwarded to the trigger's aria-label.
   */
  ariaLabel?: string;
  /**
   * Label for the "Select all" action button shown in multiple mode.
   * Defaults to localized translation. Override to customize.
   */
  selectAllLabel?: string;
  /**
   * Label for the "Select none" action button shown in multiple mode.
   * Defaults to localized translation. Override to customize.
   */
  selectNoneLabel?: string;
  /**
   * When set and the number of selected items reaches this threshold, the trigger
   * shows a localized "Selected Elements: <count>" label (via t("pixSelect.selectedCount"))
   * instead of listing the individual selections. Has no effect below the threshold.
   */
  showCounterLabelFrom?: number;
};

const props = withDefaults(defineProps<Props>(), {
  placeholder: "Select an option...",
  groups: () => [],
  items: () => [],
  multiple: false,
  hideItemIndicator: false,
  required: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: AcceptableValue];
}>();

import { computed, useId } from "vue";
import { usePortalTarget } from "@pixelpoint-pattern-library-v2/base/lib/composables/usePortalTarget";

const { t } = useI18n();

// The options render in `body`, or where an ancestor provides, e.g. a modal dialog (usePortalTarget).
const portalTarget = usePortalTarget();

// Associates the visible <label> with the trigger button via for/id.
const triggerId = useId();
const errorId = `${triggerId}-error`;
const hasError = computed(() => !!props.error);

const normalizedGroups = computed(() => {
  if (props.groups && props.groups.length > 0) {
    return props.groups;
  }
  if (props.items && props.items.length > 0) {
    return [{ items: props.items }];
  }
  return [];
});

const allItems = computed<SelectItem[]>(() => normalizedGroups.value.flatMap((g: SelectGroup) => g.items));

const activeItems = computed<SelectItem[]>(() => {
  if (props.modelValue === undefined) return [];
  const values = Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue];
  return values
    .map((v: string) => allItems.value.find((i: SelectItem) => i.value === v))
    .filter((i: SelectItem | undefined): i is SelectItem => i !== undefined);
});

const activeItem = computed<SelectItem | undefined>(() => activeItems.value[0]);

const showCounterLabel = computed<boolean>(() => {
  return props.showCounterLabelFrom !== undefined && activeItems.value.length >= props.showCounterLabelFrom;
});

const allSelected = computed<boolean>(() => {
  if (!props.multiple || props.modelValue === undefined) return false;
  const values = Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue];
  const selectableItems = allItems.value.filter((item: SelectItem) => !item.disabled);
  return selectableItems.length > 0 && selectableItems.every((item: SelectItem) => values.includes(item.value));
});

const handleSelectAll = () => {
  const selectableValues = allItems.value
    .filter((item: SelectItem) => !item.disabled)
    .map((item: SelectItem) => item.value);
  emit("update:modelValue", selectableValues);
};

const handleSelectNone = () => {
  emit("update:modelValue", []);
};
</script>

<template>
  <div class="pix-select__field">
    <label
      v-if="props.label"
      :for="triggerId"
      class="pix-select__field-label"
      :data-required="props.required || undefined"
    >
      {{ props.label }}
    </label>
    <!-- The value only when there is one: reka-ui's props take no explicit `undefined` under
         `exactOptionalPropertyTypes`, and an absent value is what an unset one means anyway. -->
    <HPixSelectRoot
      v-bind="props.modelValue === undefined ? {} : { modelValue: props.modelValue }"
      :multiple="props.multiple"
      :disabled="props.disabled"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <HPixSelectTrigger
        :id="triggerId"
        class="pix-select__trigger"
        :aria-label="props.label ? undefined : props.ariaLabel"
        :aria-required="props.required || undefined"
        :aria-invalid="hasError || undefined"
        :aria-describedby="hasError ? errorId : undefined"
        :data-error="hasError || undefined"
      >
        <HPixSelectValue :placeholder="props.placeholder" :as="$slots['trigger-value'] ? 'div' : 'span'">
          <template v-if="props.multiple && allSelected">
            {{ t("pixSelect.allSelected") }}
          </template>
          <template v-else-if="showCounterLabel">
            {{ t("pixSelect.selectedCount", { count: activeItems.length }) }}
          </template>
          <template v-else-if="$slots['trigger-value']">
            <slot
              name="trigger-value"
              :active-item="activeItem"
              :active-items="activeItems"
              :placeholder="props.placeholder"
              :all-selected="allSelected"
            />
          </template>
        </HPixSelectValue>
        <slot name="trigger-icon">
          <ChevronRightIcon class="pix-select__trigger-icon" aria-hidden="true" />
        </slot>
      </HPixSelectTrigger>

      <HPixSelectPortal :to="portalTarget">
        <HPixSelectContent :position="'popper'" class="pix-select__content">
          <HPixSelectScrollUpButton class="pix-select__scroll-up">
            <slot name="scroll-up-icon">
              <ChevronRightIcon class="pix-select__scroll-icon pix-select__scroll-icon--up" aria-hidden="true" />
            </slot>
          </HPixSelectScrollUpButton>

          <HPixSelectViewport class="pix-select__viewport">
            <slot>
              <template v-if="props.multiple">
                <HPixSelectGroup class="pix-select__actions">
                  <button type="button" class="pix-select__action-item" @click.prevent="handleSelectAll">
                    {{ props.selectAllLabel ?? t("pixSelect.selectAll") }}
                  </button>
                  <button type="button" class="pix-select__action-item" @click.prevent="handleSelectNone">
                    {{ props.selectNoneLabel ?? t("pixSelect.selectNone") }}
                  </button>
                </HPixSelectGroup>
                <HPixSelectSeparator class="pix-select__separator" />
              </template>
              <template v-for="(group, groupIndex) in normalizedGroups" :key="groupIndex">
                <HPixSelectSeparator v-if="(groupIndex as number) > 0" class="pix-select__separator" />
                <HPixSelectLabel v-if="group.label" class="pix-select__label">
                  <slot name="group-label" :label="group.label" :index="groupIndex">
                    {{ group.label }}
                  </slot>
                </HPixSelectLabel>
                <HPixSelectGroup>
                  <HPixSelectItem
                    v-for="item in group.items"
                    :key="item.value"
                    :value="item.value"
                    :disabled="item.disabled ?? false"
                    class="pix-select__item"
                    :data-hide-indicator="props.hideItemIndicator || undefined"
                  >
                    <HPixSelectItemIndicator v-if="!props.hideItemIndicator" class="pix-select__item-indicator">
                      <slot name="item-indicator">
                        <CheckIcon class="pix-select__item-indicator-icon" aria-hidden="true" />
                      </slot>
                    </HPixSelectItemIndicator>
                    <HPixSelectItemText>
                      <slot name="item-label" :item="item">
                        {{ item.label }}
                      </slot>
                    </HPixSelectItemText>
                  </HPixSelectItem>
                </HPixSelectGroup>
              </template>
            </slot>
          </HPixSelectViewport>

          <HPixSelectScrollDownButton class="pix-select__scroll-down">
            <slot name="scroll-down-icon">
              <ChevronRightIcon class="pix-select__scroll-icon pix-select__scroll-icon--down" aria-hidden="true" />
            </slot>
          </HPixSelectScrollDownButton>
        </HPixSelectContent>
      </HPixSelectPortal>
    </HPixSelectRoot>
    <span v-if="hasError" :id="errorId" class="pix-select__error" role="alert">
      {{ props.error }}
    </span>
  </div>
</template>

<style>
@layer pix-components {
  /* Defined on the component wrappers, NOT :root: custom properties resolve
     var() references against the element they're declared on, so declaring
     these on :root would freeze the values and a palette or theme scope further
     down the tree could never remap them.
     Declared on __field (the real wrapping <div>): reka-ui's SelectRoot renders
     NO DOM element (it's a context provider), so there is no root node between
     __field and the trigger to hang tokens on - an earlier attempt to do so
     left the trigger inheriting nothing, and its background fell back to
     transparent instead of the surface token. __field does wrap the trigger, so
     the tokens reach it. __content is listed separately because it renders in a
     portal outside __field. */
  .pix-select__field,
  .pix-select__content {
    /* Surface/text/border come from the semantic tokens; this component never branches on
       theme itself. */
    --_pix-select-surface: var(--pix-field-surface, var(--color-surface));
    --_pix-select-text: var(--pix-field-text, var(--color-text-on-surface));
    --_pix-select-text-muted: var(--pix-field-placeholder, var(--color-text-muted));
    --_pix-select-border-color: var(--pix-field-border-color, var(--color-surface-border));
    --_pix-select-radius: var(--pix-field-radius, var(--radius-small));

    /* Trigger geometry — previously literals in the rule, so these are the values it shipped. */
    --_pix-select-border-width: var(--pix-field-border-width, var(--border-thin));
    --_pix-select-min-height: var(--pix-field-min-height, auto);
    --_pix-select-padding-block: var(--pix-field-padding-block, var(--spacing-small));
    --_pix-select-padding-inline: var(--pix-field-padding-inline, var(--spacing-medium));
    --_pix-select-font-size: var(--pix-field-font-size, inherit);

    /* The TRIGGER is an input and belongs to the field family; the DROPDOWN is a floating panel
       and keeps --shadow-strong (see .pix-select__content). */
    --_pix-select-trigger-shadow: var(--pix-field-shadow, var(--shadow-soft));

    /* Held to the same metrics as PixTextField's label so the two line up in one form. */
    --_pix-select-label-size: var(--pix-field-label-size, var(--text-sm));
    --_pix-select-label-weight: var(--pix-field-label-weight, 500);
    --_pix-select-label-color: var(--pix-field-label-color, var(--color-text));
  }

  .pix-select__field {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xsmall);
  }

  /* No margin: like PixTextField's label, spaced by the root's flex `gap`. */
  .pix-select__field-label {
    font-size: var(--_pix-select-label-size);
    font-weight: var(--_pix-select-label-weight);
    color: var(--_pix-select-label-color);
  }

  .pix-select__field-label[data-required]::after {
    content: " *" / "";
    color: var(--color-error);
  }

  .pix-select__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-xsmall);
    width: 100%;
    min-height: var(--_pix-select-min-height);
    padding: var(--_pix-select-padding-block) var(--_pix-select-padding-inline);
    border: var(--_pix-select-border-width) solid var(--_pix-select-border-color);
    border-radius: var(--_pix-select-radius);
    background: var(--_pix-select-surface);
    color: var(--_pix-select-text);
    font-size: var(--_pix-select-font-size);
    font-weight: normal;
    box-shadow: var(--_pix-select-trigger-shadow);
    cursor: pointer;

    /* An outline, matching every other control in the library. This was a doubled box-shadow
       (a surface-coloured spacer ring plus the real one) with `outline: none`, which reads as a
       glow rather than a ring and — being a box-shadow — was silently erased by any consumer that
       flattened the trigger's shadow. That is exactly what happened downstream. */
    &:focus-visible {
      outline: var(--focus-ring-width) solid var(--focus-ring-color);
      outline-offset: var(--focus-ring-offset);
    }

    &:hover:not([data-disabled]) {
      background: var(--color-surface-hover);
    }

    [data-placeholder] {
      color: var(--_pix-select-text-muted);
    }
  }

  /* Re-points the border token on the trigger itself rather than overriding the
     border-color declaration, so the invalid state stays a token flip. */
  .pix-select__trigger[data-error] {
    --_pix-select-border-color: var(--color-error);
  }

  .pix-select__error {
    font-size: var(--text-sm);
    color: var(--color-error);
  }

  .pix-select__trigger[data-disabled] {
    cursor: not-allowed;
    opacity: var(--opacity-disabled-state);
  }

  .pix-select__trigger-icon {
    height: 1rem;
    width: 1rem;
    flex-shrink: 0;
    rotate: 90deg;
    color: var(--_pix-select-text-muted);
  }

  .pix-select__content {
    position: relative;
    z-index: 50;
    min-width: var(--reka-select-trigger-width);
    overflow: hidden;
    border-radius: var(--_pix-select-radius);
    border: var(--_pix-select-border-width) solid var(--_pix-select-border-color);
    background: var(--_pix-select-surface);
    box-shadow: var(--shadow-strong);
  }

  .pix-select__viewport {
    max-width: 90vw;
    max-height: var(--reka-select-content-available-height);
    padding: var(--spacing-small);
  }

  .pix-select__separator {
    margin-block: var(--spacing-small);
    height: var(--border-thin);
    background: var(--_pix-select-border-color);
  }

  .pix-select__label {
    padding: var(--spacing-small);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--_pix-select-text-muted);
  }

  .pix-select__item {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--spacing-small);
    padding-block: var(--spacing-xsmall);
    padding-inline-start: var(--spacing-xlarge);
    cursor: pointer;
    user-select: none;
    color: var(--_pix-select-text);
    border-radius: var(--_pix-select-radius);

    &[data-disabled] {
      pointer-events: none;
      color: var(--_pix-select-text-muted);
      opacity: var(--opacity-disabled-state);
    }

    &[data-highlighted] {
      outline: none;
      background: var(--color-highlight);
    }

    &[data-hide-indicator] {
      padding-inline-start: var(--spacing-xsmall);
    }
  }

  .pix-select__item-indicator {
    position: absolute;
    left: 0.5rem;
    display: flex;
    align-items: center;
  }

  .pix-select__item-indicator-icon {
    height: 0.75rem;
    width: 0.75rem;
    flex-shrink: 0;
    color: var(--_pix-select-text);
  }

  .pix-select__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .pix-select__action-item {
    display: block;
    flex: 1;
    min-width: fit-content;
    padding: var(--spacing-xsmall) var(--spacing-small);
    text-align: center;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--_pix-select-surface);
    background: var(--color-primary);
    border: none;
    border-radius: var(--_pix-select-radius);
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }

    &:focus-visible {
      outline: none;
      opacity: 0.9;
    }
  }

  .pix-select__scroll-up,
  .pix-select__scroll-down {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-block: var(--spacing-xsmall);
    cursor: default;
    color: var(--_pix-select-text-muted);
  }

  .pix-select__scroll-icon {
    height: 1rem;
    width: 1rem;
  }

  .pix-select__scroll-icon--up {
    rotate: -90deg;
  }

  .pix-select__scroll-icon--down {
    rotate: 90deg;
  }
}
</style>
